import { Button, message, Popconfirm, Space, Tag } from 'antd';
import { observer } from 'mobx-react';
import { RoleType } from 'permission/role/type/roleType';
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { accountStore } from 'store/accountStore';
import * as Component from '../../../../shared/components';
import { userService } from '../service/userService';
import { UserDetailType } from '../types/userType';

const UserPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const canDelete = accountStore.hasPermission('user', 'delete');

  const fetchUser = async (
    params: URLSearchParams
  ): Promise<{ data: UserDetailType[]; count: number }> => {
    const userListWithRole = await userService.fetchUserListWithRole(params);
    return { data: userListWithRole.data, count: userListWithRole.count };
  };

  const handleDelete = async (record: UserDetailType): Promise<void> => {
    setDeletingId(record.id);
    try {
      await userService.deleteUser(record.id);
      message.success(`User "${record.username}" has been deleted.`);
      setRefreshCounter(prev => prev + 1);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to delete user.';
      message.error(errorMessage);
    } finally {
      setDeletingId(null);
    }
  };

  const columns = [
    {
      title: 'User ID',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Organization',
      dataIndex: 'company',
      key: 'company_id',
      render: (_text: string, record: UserDetailType): React.ReactNode => {
        return <div>{record.company?.title || '-'}</div>;
      },
    },
    {
      title: 'Role',
      dataIndex: 'roles',
      key: 'roles',
      render: (_text: string, record: UserDetailType): React.ReactNode => {
        return (
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
            {record.roles.map((role: RoleType) => (
              <Tag key={role.id} color="blue">
                {role.name}
              </Tag>
            ))}
          </div>
        );
      },
    },
    {
      title: 'Actions',
      key: 'action',
      dataIndex: 'action',
      render: (_text: string, record: UserDetailType): React.ReactNode => (
        <Space size="middle">
          <Button key="edit" onClick={() => navigate(`/user/edit/${record.id}`)}>
            Edit
          </Button>
          {canDelete && (
            <Popconfirm
              key="delete"
              title="Delete user"
              description={`Are you sure you want to delete "${record.username}"? This cannot be undone.`}
              okText="Delete"
              okButtonProps={{ danger: true }}
              cancelText="Cancel"
              onConfirm={() => handleDelete(record)}
            >
              <Button danger loading={deletingId === record.id}>
                Delete
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const AddButton = (): JSX.Element => {
    return (
      <Component.CustomButtonComponent
        text={`+ Add User`}
        onClick={() => navigate('/user/add')}
        size={'small'}
      />
    );
  };

  return (
    <Component.BasePageComponent>
      <Component.TableComponent<UserDetailType>
        key={refreshCounter}
        name="User"
        searchParams={searchParams}
        setSearchParams={setSearchParams}
        columns={columns}
        dataFetch={fetchUser}
        additionalButton={[AddButton()]}
      />
    </Component.BasePageComponent>
  );
};

export default observer(UserPage);
